import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoGridOutline, IoArrowForward, IoPeopleOutline } from "react-icons/io5";
import styles from "./ExperienceBuilder.module.css";
import { getSections } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";

export default function ExperienceBuilder() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ pages: 0, categories: 0, live: 0 });

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        // "Pages" already reflects the real total via the API's own count --
        // "Sections across app" and "Live" need every row to match, not just
        // the first page of 500, so keep paging until they're all in.
        const limit = 500;
        let page = 1;
        let lastPage = 1;
        let total = 0;
        let rows = [];
        do {
          const response = await getSections(page, limit);
          rows = rows.concat(response.data?.data || []);
          total = response.data?.total ?? rows.length;
          lastPage = response.data?.last_page ?? 1;
          page += 1;
        } while (page <= lastPage && !cancelled);

        if (cancelled) return;
        const categories = rows.reduce((sum, s) => sum + (s.categories?.length || 0), 0);
        const live = rows.filter((s) => s.is_active).length;
        setStats({ pages: total, categories, live });
      } catch (err) {
        if (!cancelled) {
          showToast(err?.response?.data?.message || "Error fetching stats", "error");
        }
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Experience Builder</h1>
      <p className={styles.subtitle}>
        Design and manage the pages of your OTT app — no code required
      </p>

      <div className={styles.heroCard}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIcon}>
            <IoGridOutline />
          </div>
          <div className={styles.heroText}>
            <h2 className={styles.heroHeading}>Open the Experience Builder</h2>
            <p className={styles.heroDescription}>
              Drag-and-drop sections, edit inline, and preview live across Web, TV and Mobile — then publish.
            </p>
          </div>
        </div>
        <button
          className={styles.heroButton}
          onClick={() => navigate("/home-page-builder")}
        >
          Open Builder <IoArrowForward />
        </button>
      </div>

      {/* Creator Order lives beside the page builder, not inside it: it
          arranges the public Creators directory (users), not page sections. */}
      <div className={styles.heroCard}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIcon}>
            <IoPeopleOutline />
          </div>
          <div className={styles.heroText}>
            <h2 className={styles.heroHeading}>Creator Order</h2>
            <p className={styles.heroDescription}>
              Choose the order creators appear in on your Creators page — drag
              them, or send one straight to the top or bottom.
            </p>
          </div>
        </div>
        <button
          className={styles.heroButton}
          onClick={() => navigate("/mainlayout/creator-order")}
        >
          Arrange Creators <IoArrowForward />
        </button>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{stats.pages}</div>
          <div className={styles.statLabel}>Pages</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{stats.categories}</div>
          <div className={styles.statLabel}>Sections across app</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{stats.live}</div>
          <div className={styles.statLabel}>Live in navigation</div>
        </div>
      </div>
    </div>
  );
}
